import { useEffect, useState } from 'react';
import { api, type ScheduledPublication } from '../lib/api';

const TONE: Record<ScheduledPublication['status'], string> = {
  published: 'var(--green)', uploading: 'var(--amber)',
  queued: 'var(--ink-2)', failed: 'var(--red)',
};

function when(p: ScheduledPublication): string {
  if (p.status === 'published' && p.published_at) return new Date(p.published_at).toLocaleString();
  if (!p.scheduled_for) return 'next run';
  const d = new Date(p.scheduled_for);
  return d > new Date() ? d.toLocaleString() : `${d.toLocaleString()} — due`;
}

/**
 * Why a row is not moving. A producer looking at a stalled schedule needs the
 * actual reason, not a spinner: the publisher only claims destinations that
 * hold a token, so an unconnected account waits forever and silently.
 */
function blocker(p: ScheduledPublication): string | null {
  if (p.status !== 'queued') return null;
  if (!p.connector_ready) return 'not connected — this account has no token yet';
  if (p.render_status !== 'complete') return `waiting on the render (${p.render_status ?? 'not started'})`;
  return null;
}

export function Schedule() {
  const [rows, setRows] = useState<ScheduledPublication[] | null>(null);
  const [blocked, setBlocked] = useState(0);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try { const r = await api.publications(); setRows(r.publications); setBlocked(r.blocked); }
    catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { void load(); }, []);

  const upcoming = (rows ?? []).filter((p) => p.status !== 'published');
  const history = (rows ?? []).filter((p) => p.status === 'published');

  // The comment here used to say "only while something is in flight" above an
  // unconditional setInterval that polled every fifteen seconds for as long as
  // the tab was open, on a screen that is usually all history. The comment
  // described the intent; the code did not implement it.
  const inFlight = upcoming.length > 0;
  useEffect(() => {
    if (!inFlight) return;
    const t = window.setInterval(() => void load(), 15000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inFlight]);

  return (
    <>
      <div className="vhead" data-move="publish-failed">
        <div>
          <h2>Schedule</h2>
          <p>
            Everything queued and everything that has gone out, across every card and brand.
            One row per destination, because a cut can succeed on one platform and fail on another.
          </p>
        </div>
      </div>

      {error && <div className="err">{error}</div>}

      {blocked > 0 && (
        <div className="err" style={{ borderColor: 'var(--amber)', color: 'var(--amber)' }}>
          {blocked} scheduled {blocked === 1 ? 'destination has' : 'destinations have'} no connected
          account. The publisher skips those rather than failing them, so they will wait
          indefinitely — connect the account and they go out on the next run.
        </div>
      )}

      <div className="panel">
        <div className="panel-hd">
          <h3>Upcoming</h3><span className="chip">{upcoming.length}</span>
        </div>
        {upcoming.length === 0 ? (
          <div className="empty">Nothing scheduled. Schedule a card from its Review stage.</div>
        ) : (
          <table>
            <thead>
              <tr><th>Card</th><th>Destination</th><th>When</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {upcoming.map((p) => {
                const why = blocker(p);
                return (
                  <tr key={p.id}>
                    <td>
                      <b>{p.card_title}</b>
                      {p.brand_name && <div className="sub-dim">{p.brand_name}</div>}
                    </td>
                    <td>
                      {p.platform}
                      {p.account_handle && <div className="sub-dim">{p.account_handle}</div>}
                    </td>
                    <td style={{ color: 'var(--ink-2)', fontSize: 12.5 }}>{when(p)}</td>
                    <td style={{ color: TONE[p.status], fontWeight: 500 }}>
                      {p.status}
                      {p.attempts > 1 && p.status !== 'published' ? ` · attempt ${p.attempts}` : ''}
                    </td>
                    <td style={{ fontSize: 12, color: why ? 'var(--amber)' : 'var(--red)' }}>
                      {why ?? p.error ?? ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="panel" style={{ marginTop: 16 }}>
        <div className="panel-hd"><h3>Published</h3><span className="chip">{history.length}</span></div>
        {history.length === 0 ? (
          <div className="empty">Nothing has gone out yet.</div>
        ) : (
          <table>
            <thead><tr><th>Card</th><th>Destination</th><th>Published</th><th /></tr></thead>
            <tbody>
              {history.map((p) => (
                <tr key={p.id}>
                  <td><b>{p.card_title}</b></td>
                  <td>{p.platform}{p.account_handle ? ` · ${p.account_handle}` : ''}</td>
                  <td style={{ color: 'var(--ink-2)', fontSize: 12.5 }}>{when(p)}</td>
                  <td className="r">
                    {p.remote_url && <a href={p.remote_url} target="_blank" rel="noreferrer">view</a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
